import type {
  WorkExperienceBullet,
  WorkExperienceCompany,
  WorkExperienceRole,
} from '@applicator/shared'

export type WorkExperienceGroupMode = 'company' | 'position'

const SPLIT_ID_SEP = '__'

/** Stable company id when a role is shown as its own block (group by position). */
export function splitWorkExperienceCompanyId(companyId: string, roleId: string): string {
  const base = getWorkExperienceBaseCompanyId(companyId)
  return `${base}${SPLIT_ID_SEP}${roleId}`
}

export function getWorkExperienceBaseCompanyId(companyId: string): string {
  const idx = companyId.indexOf(SPLIT_ID_SEP)
  return idx >= 0 ? companyId.slice(0, idx) : companyId
}

function dedupeRoleBullets(role: WorkExperienceRole): WorkExperienceRole {
  const bullets = role.bulletPoints ?? []
  if (bullets.length <= 1) return role

  const byId = new Map<string, WorkExperienceBullet>()
  const withoutId: WorkExperienceBullet[] = []
  for (const bullet of bullets) {
    if (bullet.id) {
      byId.set(bullet.id, bullet)
    } else {
      withoutId.push(bullet)
    }
  }

  const deduped = [...withoutId, ...byId.values()]
  if (deduped.length === bullets.length) return role
  return { ...role, bulletPoints: deduped }
}

function normalizeRoleForGrouping(role: WorkExperienceRole): WorkExperienceRole {
  return dedupeRoleBullets(role)
}

/** Last occurrence wins — keeps position-mode preview rows unique by role id. */
export function dedupeRolesById(roles: WorkExperienceRole[]): WorkExperienceRole[] {
  const byId = new Map<string, WorkExperienceRole>()
  const withoutId: WorkExperienceRole[] = []

  for (const role of roles) {
    const normalized = normalizeRoleForGrouping(role)
    if (role.id) {
      byId.set(role.id, normalized)
    } else {
      withoutId.push(normalized)
    }
  }

  return [...withoutId, ...byId.values()]
}

function mergeRolesUnique(existing: WorkExperienceRole[], incoming: WorkExperienceRole[]): WorkExperienceRole[] {
  const merged = dedupeRolesById(existing.map(normalizeRoleForGrouping))
  const indexById = new Map(merged.map((role, index) => [role.id, index]))

  for (const role of incoming) {
    const normalized = normalizeRoleForGrouping(role)
    if (!role.id) {
      merged.push(normalized)
      continue
    }

    const existingIndex = indexById.get(role.id)
    if (existingIndex === undefined) {
      indexById.set(role.id, merged.length)
      merged.push(normalized)
    } else {
      const previous = merged[existingIndex]!
      merged[existingIndex] = dedupeRoleBullets({
        ...normalized,
        bulletPoints: [...(previous.bulletPoints ?? []), ...(normalized.bulletPoints ?? [])],
      })
    }
  }

  return dedupeRolesById(merged)
}

/**
 * Merge split blocks (one role per company row) back into one company per base id.
 * Preserves first-seen company order; appends roles in encounter order.
 */
export function groupWorkExperienceByCompany(
  companies: WorkExperienceCompany[]
): WorkExperienceCompany[] {
  const order: string[] = []
  const byBase = new Map<string, WorkExperienceCompany>()

  for (const company of companies) {
    const baseId = getWorkExperienceBaseCompanyId(company.id)
    const roles = dedupeRolesById((company.roles ?? []).map(normalizeRoleForGrouping))
    const existing = byBase.get(baseId)

    if (!existing) {
      byBase.set(baseId, {
        ...company,
        id: baseId,
        roles: [...roles],
      })
      order.push(baseId)
      continue
    }

    existing.roles = mergeRolesUnique(existing.roles ?? [], roles)
    if (!existing.description?.trim() && company.description?.trim()) {
      existing.description = company.description
    }
  }

  return order.map((baseId) => {
    const company = byBase.get(baseId)!
    return {
      ...company,
      roles: dedupeRolesById(company.roles ?? []),
    }
  })
}

/**
 * One preview/editor row per role; company name and description repeat per period.
 */
export function groupWorkExperienceByPosition(
  companies: WorkExperienceCompany[]
): WorkExperienceCompany[] {
  const merged = groupWorkExperienceByCompany(companies)
  const result: WorkExperienceCompany[] = []

  for (const company of merged) {
    const roles = dedupeRolesById(company.roles ?? [])
    if (roles.length === 0) {
      result.push({ ...company, roles: [] })
      continue
    }
    for (const role of roles) {
      result.push({
        ...company,
        id: splitWorkExperienceCompanyId(company.id, role.id),
        roles: [role],
      })
    }
  }

  return result
}

export function applyWorkExperienceGroupMode(
  companies: WorkExperienceCompany[],
  groupMode: WorkExperienceGroupMode
): WorkExperienceCompany[] {
  return groupMode === 'position'
    ? groupWorkExperienceByPosition(companies)
    : groupWorkExperienceByCompany(companies)
}

/** Dedupe role and bullet ids within each company row (editor + persisted content). */
export function sanitizeWorkExperienceCompanies(
  companies: WorkExperienceCompany[],
): WorkExperienceCompany[] {
  return companies.map((company) => ({
    ...company,
    roles: mergeRolesUnique([], company.roles ?? []),
  }))
}
